import { ActionIcon, Alert, Badge, Button, Group, Loader, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconAlertTriangle, IconBrain, IconCheck, IconSearch, IconSend, IconSparkles } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  applyKnowledgeReviewPatch,
  listKnowledgeReviewMessages,
  saveKnowledgeReviewMemory,
  streamKnowledgeReviewChat,
  type KnowledgeReviewMessage,
  type KnowledgeUnitDetail,
} from '@mygpt/app/apis/services/knowledgeLoop';
import { charDiff, type DiffSeg } from '@/components/ZendeskOps/shared';
import { CARD_BORDER, errorDetail } from './shared';

interface Props {
  datasetId: string;
  unit: KnowledgeUnitDetail;
  onUnitChanged: (unit: KnowledgeUnitDetail) => void;
}

const Diff = ({ segs }: { segs: DiffSeg[] }) => (
  <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
    {segs.map((seg, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        style={
          seg.type === 'del'
            ? { background: '#FFE3E3', textDecoration: 'line-through', color: '#C92A2A' }
            : seg.type === 'ins'
            ? { background: '#D3F9D8', color: '#2B8A3E' }
            : undefined
        }
      >
        {seg.text}
      </span>
    ))}
  </Text>
);

// 1 本の差分にまとめる（削除と追加を同じ流れで見せる）
const mergedDiff = (before: string, after: string): DiffSeg[] => {
  const { draft, final } = charDiff(before, after);
  const out: DiffSeg[] = [];
  let i = 0;
  let j = 0;
  while (i < draft.length || j < final.length) {
    const a = draft[i];
    const b = final[j];
    if (a && a.type === 'del') {
      out.push(a);
      i++;
    } else if (b && b.type === 'ins') {
      out.push(b);
      j++;
    } else {
      if (a) out.push(a);
      i++;
      j++;
    }
  }
  return out;
};

/** 審査ドロワー右側の AI 対話。AI は提案するだけで、適用・記憶はここのボタンを押したときだけ行われる */
export const ReviewChat = ({ datasetId, unit, onUnitChanged }: Props) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<KnowledgeReviewMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [lookups, setLookups] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [memoryText, setMemoryText] = useState<Record<string, string>>({});
  // 修正カードの手直し（message id → 項目）。未編集の項目は AI の提案どおり
  const [patchEdits, setPatchEdits] = useState<Record<string, { title?: string; body?: string; similar?: string }>>({});
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMessages([]);
    listKnowledgeReviewMessages(datasetId, unit.id)
      .then(({ data }) => alive && setMessages(data.items || []))
      .catch((e) => console.error('load review messages failed:', e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      abortRef.current?.abort();
    };
  }, [datasetId, unit.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, draft, lookups]);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || streaming) return;
      setInput('');
      setDraft('');
      setLookups([]);
      setStreaming(true);
      setMessages((list) => [
        ...list,
        { id: `local-${Date.now()}`, role: 'user', content, patch_applied: false, memory_saved: false },
      ]);
      const controller = new AbortController();
      abortRef.current = controller;
      await streamKnowledgeReviewChat(
        datasetId,
        unit.id,
        content,
        {
          onDelta: (piece) => setDraft((d) => d + piece),
          onLookup: (step) => setLookups((l) => [...l, step.query]),
          onDone: (message) => {
            setMessages((list) => [...list, message]);
            setDraft('');
          },
          onError: () => {
            setDraft('');
            showNotification({ color: 'red', message: t('knowledge_loop.chat.error') });
          },
        },
        controller.signal
      );
      setStreaming(false);
    },
    [datasetId, unit.id, streaming, t]
  );

  const replace = (message: KnowledgeReviewMessage) =>
    setMessages((list) => list.map((m) => (m.id === message.id ? message : m)));

  const editedFields = (m: KnowledgeReviewMessage) => {
    const e = patchEdits[m.id];
    const f = m.proposed_patch?.fields;
    if (!e || !f) return undefined;
    const out: { title?: string; body?: string; similar_questions?: string[] } = {};
    if (e.title !== undefined && f.title && e.title !== f.title) out.title = e.title;
    if (e.body !== undefined && f.body && e.body !== f.body) out.body = e.body;
    if (e.similar !== undefined && f.similar_questions) {
      const list = e.similar
        .split('/')
        .map((q) => q.trim())
        .filter(Boolean);
      if (list.join('/') !== f.similar_questions.join('/')) out.similar_questions = list;
    }
    return Object.keys(out).length ? out : undefined;
  };

  const applyPatch = async (m: KnowledgeReviewMessage) => {
    setBusy(`patch-${m.id}`);
    try {
      const { data } = await applyKnowledgeReviewPatch(datasetId, unit.id, m.id, editedFields(m));
      replace(data.message);
      onUnitChanged(data.unit);
      showNotification({ color: 'green', message: t('knowledge_loop.chat.patch_applied') });
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.review.error') });
    } finally {
      setBusy(null);
    }
  };

  const saveMemory = async (m: KnowledgeReviewMessage) => {
    setBusy(`memory-${m.id}`);
    try {
      const { data } = await saveKnowledgeReviewMemory(datasetId, unit.id, m.id, memoryText[m.id]?.trim() || undefined);
      replace(data.message);
      showNotification({ color: 'green', message: t('knowledge_loop.chat.memory_saved') });
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.review.error') });
    } finally {
      setBusy(null);
    }
  };

  const editable = unit.status === 'candidate' || unit.status === 'approved';
  const presets = [
    t('knowledge_loop.chat.preset_why'),
    t('knowledge_loop.chat.preset_conflict'),
    t('knowledge_loop.chat.preset_bullets'),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Group gap={6} px="md" py={10} style={{ borderBottom: CARD_BORDER, flexShrink: 0 }}>
        <IconSparkles size={15} color="#7048E8" />
        <Text fw={600} size="sm">
          {t('knowledge_loop.chat.title')}
        </Text>
      </Group>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Stack gap="sm">
            {messages.length === 0 && !streaming && (
              <Stack gap={8} py="md">
                <Text size="xs" c="gray.6">
                  {t('knowledge_loop.chat.intro')}
                </Text>
                {presets.map((p) => (
                  <Button
                    key={p}
                    size="xs"
                    variant="light"
                    color="violet"
                    justify="flex-start"
                    onClick={() => send(p)}
                    styles={{
                      label: { whiteSpace: 'normal', textAlign: 'left' },
                      root: { height: 'auto', padding: 8 },
                    }}
                  >
                    {p}
                  </Button>
                ))}
              </Stack>
            )}

            {messages.map((m) => {
              const patch = m.proposed_patch;
              const mem = m.memory_proposal;
              return (
                <Stack key={m.id} gap={6} align={m.role === 'user' ? 'flex-end' : 'stretch'}>
                  <div
                    style={{
                      maxWidth: '92%',
                      padding: '8px 10px',
                      borderRadius: 10,
                      background: m.role === 'user' ? '#F3F0FF' : '#F8F9FA',
                    }}
                  >
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                      {m.content}
                    </Text>
                  </div>

                  {patch && (
                    <div style={{ border: '1px solid #B2F2BB', borderRadius: 10, overflow: 'hidden' }}>
                      <Group justify="space-between" px={10} py={6} style={{ background: '#EBFBEE' }}>
                        <Text size="xs" fw={600}>
                          {t('knowledge_loop.chat.patch_title')}
                        </Text>
                        {m.patch_applied && (
                          <Badge size="xs" color="green" variant="light" leftSection={<IconCheck size={10} />}>
                            {t(
                              patch.edited_by_reviewer
                                ? 'knowledge_loop.chat.edited_applied'
                                : 'knowledge_loop.chat.applied'
                            )}
                          </Badge>
                        )}
                      </Group>
                      <Stack gap={6} p={10}>
                        {patch.summary && (
                          <Text size="xs" c="gray.7">
                            {patch.summary}
                          </Text>
                        )}
                        {(() => {
                          const canEdit = !m.patch_applied && editable;
                          const e = patchEdits[m.id] || {};
                          const curTitle = e.title ?? patch.fields.title ?? '';
                          const curBody = e.body ?? patch.fields.body ?? '';
                          const curSimilar = e.similar ?? (patch.fields.similar_questions || []).join(' / ');
                          const setEdit = (k: 'title' | 'body' | 'similar', v: string) =>
                            setPatchEdits((all) => ({ ...all, [m.id]: { ...(all[m.id] || {}), [k]: v } }));
                          return (
                            <>
                              {canEdit && (
                                <Text size="xs" c="gray.6">
                                  {t('knowledge_loop.chat.edit_hint')}
                                </Text>
                              )}
                              {patch.fields.title &&
                                (canEdit ? (
                                  <TextInput
                                    size="xs"
                                    label={t('knowledge_loop.review.field_title')}
                                    value={curTitle}
                                    onChange={(ev) => setEdit('title', ev.currentTarget.value)}
                                  />
                                ) : null)}
                              {patch.fields.title && (
                                <Diff segs={mergedDiff(m.patch_applied ? patch.fields.title : unit.title, curTitle)} />
                              )}
                              {patch.fields.body &&
                                (canEdit ? (
                                  <Textarea
                                    size="xs"
                                    label={t('knowledge_loop.review.field_body')}
                                    autosize
                                    minRows={3}
                                    maxRows={10}
                                    value={curBody}
                                    onChange={(ev) => setEdit('body', ev.currentTarget.value)}
                                  />
                                ) : null)}
                              {patch.fields.body && (
                                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                  <Diff segs={mergedDiff(m.patch_applied ? patch.fields.body : unit.body, curBody)} />
                                </div>
                              )}
                              {patch.fields.similar_questions &&
                                (canEdit ? (
                                  <TextInput
                                    size="xs"
                                    label={t('knowledge_loop.review.field_similar')}
                                    description="A / B / C"
                                    value={curSimilar}
                                    onChange={(ev) => setEdit('similar', ev.currentTarget.value)}
                                  />
                                ) : (
                                  <Text size="xs" c="gray.7">
                                    {t('knowledge_loop.review.field_similar')}：
                                    {patch.fields.similar_questions.join(' / ')}
                                  </Text>
                                ))}
                            </>
                          );
                        })()}
                        {!!patch.unsupported?.length && (
                          <Alert color="yellow" variant="light" p={8} icon={<IconAlertTriangle size={14} />}>
                            <Text size="xs" fw={600}>
                              {t('knowledge_loop.chat.unsupported')}
                            </Text>
                            {patch.unsupported.map((u) => (
                              <Text key={u} size="xs">
                                ・{u}
                              </Text>
                            ))}
                          </Alert>
                        )}
                        {!m.patch_applied && (
                          <Button
                            size="xs"
                            color="green"
                            disabled={!editable}
                            loading={busy === `patch-${m.id}`}
                            onClick={() => applyPatch(m)}
                          >
                            {t('knowledge_loop.chat.apply_patch')}
                          </Button>
                        )}
                      </Stack>
                    </div>
                  )}

                  {mem && (
                    <div style={{ border: '1px solid #D0BFFF', borderRadius: 10, overflow: 'hidden' }}>
                      <Group justify="space-between" px={10} py={6} style={{ background: '#F3F0FF' }}>
                        <Group gap={4}>
                          <IconBrain size={13} color="#7048E8" />
                          <Text size="xs" fw={600}>
                            {t('knowledge_loop.chat.memory_title')}
                          </Text>
                        </Group>
                        {m.memory_saved && (
                          <Badge size="xs" color="violet" variant="light" leftSection={<IconCheck size={10} />}>
                            {t('knowledge_loop.chat.saved')}
                          </Badge>
                        )}
                      </Group>
                      <Stack gap={6} p={10}>
                        {m.memory_saved ? (
                          <Text size="xs">{mem.text}</Text>
                        ) : (
                          <>
                            <Textarea
                              size="xs"
                              autosize
                              minRows={2}
                              maxLength={300}
                              value={memoryText[m.id] ?? mem.text}
                              onChange={(e) => setMemoryText((s) => ({ ...s, [m.id]: e.currentTarget.value }))}
                            />
                            <Text size="xs" c="gray.6">
                              {t('knowledge_loop.chat.memory_desc')}
                            </Text>
                            <Button
                              size="xs"
                              color="violet"
                              variant="light"
                              loading={busy === `memory-${m.id}`}
                              onClick={() => saveMemory(m)}
                            >
                              {t('knowledge_loop.chat.save_memory')}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </div>
                  )}
                </Stack>
              );
            })}

            {streaming && (
              <Stack gap={4}>
                {lookups.map((q) => (
                  <Group key={q} gap={4}>
                    <IconSearch size={12} color="#868E96" />
                    <Text size="xs" c="gray.6">
                      {t('knowledge_loop.chat.looking_up', { query: q })}
                    </Text>
                  </Group>
                ))}
                <div style={{ padding: '8px 10px', borderRadius: 10, background: '#F8F9FA' }}>
                  {draft ? (
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                      {draft}
                    </Text>
                  ) : (
                    <Loader size="xs" type="dots" />
                  )}
                </div>
              </Stack>
            )}
            <div ref={bottomRef} />
          </Stack>
        )}
      </div>

      <div style={{ borderTop: CARD_BORDER, padding: 10, flexShrink: 0 }}>
        <Group gap={6} align="flex-end" wrap="nowrap">
          <Textarea
            style={{ flex: 1 }}
            size="sm"
            autosize
            minRows={1}
            maxRows={5}
            placeholder={t('knowledge_loop.chat.placeholder')}
            value={input}
            disabled={streaming}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send(input);
              }
            }}
          />
          <ActionIcon
            size="lg"
            color="violet"
            variant="filled"
            disabled={!input.trim()}
            loading={streaming}
            onClick={() => send(input)}
            aria-label={t('knowledge_loop.chat.send')}
          >
            <IconSend size={16} />
          </ActionIcon>
        </Group>
        <Text size="xs" c="gray.5" mt={4}>
          {t('knowledge_loop.chat.footnote')}
        </Text>
      </div>
    </div>
  );
};
